import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, AlertTriangle, X, Check, Loader2 } from "lucide-react";

const currencyFormat = (value, currency = "GBP") => {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch (e) {
    return `${currency} ${n.toFixed(2)}`;
  }
};

const statusMeta = (days, status) => {
  if (status === "overdue" || days < 0) {
    return {
      label: `${Math.abs(days)}d late`,
      color: "#FF6B6B",
      urgent: true,
    };
  }
  if (days === 0) return { label: "due today", color: "#F5D742", urgent: true };
  if (days === 1) return { label: "due tomorrow", color: "#F5D742", urgent: false };
  return { label: `due in ${days}d`, color: "#00E676", urgent: false };
};

const DueSoonBanner = ({ reminders = [], withinDays = 2, onDismiss, onSeeCard, onSnooze }) => {
  const items = (reminders || []).slice(0, 4);
  const hasAny = items.length > 0;
  const anyOverdue = items.some((r) => r.status === "overdue" || r.days_until < 0);
  const [snoozingId, setSnoozingId] = useState(null);
  const upcomingLabel =
    withinDays === 1
      ? "Due within 1 day"
      : withinDays === 0
      ? "Due today"
      : `Due within ${withinDays} days`;

  const handleSnooze = async (cardId) => {
    if (!onSnooze || snoozingId) return;
    setSnoozingId(cardId);
    try {
      await onSnooze(cardId);
    } finally {
      setSnoozingId(null);
    }
  };

  return (
    <AnimatePresence>
      {hasAny && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: [0.2, 0.65, 0.3, 1] }}
          className={`relative overflow-hidden rounded-2xl border p-5 lg:p-6 mb-8 ${
            anyOverdue
              ? "border-[#FF6B6B]/40 bg-[#FF6B6B]/[0.06]"
              : "border-[#F5D742]/40 bg-[#F5D742]/[0.06]"
          }`}
          data-testid="due-soon-banner"
        >
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-40"
            style={{ background: anyOverdue ? "#FF6B6B" : "#F5D742" }}
          />

          <div className="flex items-start gap-4 relative">
            <div
              className={`w-11 h-11 rounded-xl border flex items-center justify-center flex-shrink-0 ${
                anyOverdue
                  ? "border-[#FF6B6B]/50 bg-[#FF6B6B]/[0.12] text-[#FF6B6B]"
                  : "border-[#F5D742]/50 bg-[#F5D742]/[0.12] text-[#F5D742]"
              }`}
            >
              {anyOverdue ? <AlertTriangle size={18} /> : <Bell size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold"
                    style={{ color: anyOverdue ? "#FF6B6B" : "#F5D742" }}
                    data-testid="due-soon-title"
                  >
                    {anyOverdue ? "Payment overdue" : upcomingLabel}
                  </div>
                  <h3
                    className="mt-1.5 text-white text-lg font-semibold tracking-tight"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {items.length === 1
                      ? "1 credit card needs your attention"
                      : `${items.length} credit cards need your attention`}
                  </h3>
                </div>
                {onDismiss && (
                  <button
                    onClick={onDismiss}
                    className="text-slate-400 hover:text-white transition-colors p-1"
                    aria-label="Dismiss"
                    data-testid="due-soon-dismiss"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <ul className="mt-4 space-y-2" data-testid="due-soon-list">
                {items.map((r) => {
                  const meta = statusMeta(r.days_until, r.status);
                  return (
                    <li
                      key={r.card_id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.03] px-4 py-2.5"
                      data-testid={`due-soon-item-${r.card_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[10px] font-bold tracking-widest flex-shrink-0"
                          style={{
                            background: `${meta.color}22`,
                            border: `1px solid ${meta.color}55`,
                            color: meta.color,
                          }}
                        >
                          {r.last_four ? `••${r.last_four.slice(-2)}` : "CC"}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm text-white font-semibold truncate">{r.name}</div>
                          <div className="text-[11px] text-slate-300">
                            {new Date(r.due_date).toLocaleDateString("en-US", { month: "short", day: "2-digit" })}
                            {r.balance !== undefined && r.balance !== null && (
                              <> · {currencyFormat(r.balance, r.currency)} owing</>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center flex-wrap gap-2 sm:flex-shrink-0 sm:justify-end pl-11 sm:pl-0">
                        <span
                          className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full font-bold whitespace-nowrap"
                          style={{
                            color: meta.color,
                            background: `${meta.color}18`,
                            border: `1px solid ${meta.color}55`,
                          }}
                        >
                          {meta.label}
                        </span>
                        {onSnooze && (
                          <button
                            onClick={() => handleSnooze(r.card_id)}
                            disabled={snoozingId === r.card_id}
                            className="inline-flex items-center gap-1 rounded-full border border-[#00E676]/40 hover:border-[#00E676] bg-[#00E676]/[0.08] hover:bg-[#00E676]/[0.16] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#00E676] transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                            data-testid={`due-soon-snooze-${r.card_id}`}
                            title="Clear from this cycle's reminders"
                          >
                            {snoozingId === r.card_id ? (
                              <Loader2 size={10} className="animate-spin" />
                            ) : (
                              <Check size={10} />
                            )}
                            I've paid this
                          </button>
                        )}
                        {onSeeCard && (
                          <button
                            onClick={() => onSeeCard(r.card_id)}
                            className="text-[11px] text-slate-300 hover:text-white transition-colors underline underline-offset-2 whitespace-nowrap"
                            data-testid={`due-soon-view-${r.card_id}`}
                          >
                            View
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DueSoonBanner;
